import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  Info,
} from '@mui/icons-material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';

interface SharedResult {
  id: number;
  name: string;
  age: number;
  gender: string;
  sport: string;
  years_of_experience: number;
  competition_level: string;
  basic_abilities: { [key: string]: number };
  riasec_profile: { [key: string]: number };
  work_values: { [key: string]: number };
  portable_skills: { [key: string]: number };
  strengths: string[];
  development_areas: string[];
  athlete_advantages: string[];
  career_matches: CareerMatch[];
  created_at: string;
}

interface CareerMatch {
  career: {
    id: string;
    name: string;
    category: string;
    description: string;
    averageSalary: string;
    athleteFriendly: boolean;
  };
  matchScore: number;
  reason: string;
  nextSteps: string[];
}

const SharedResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<SharedResult | null>(null);

  useEffect(() => {
    if (!id) {
      setError('結果IDが指定されていません');
      setLoading(false);
      return;
    }

    const fetchResult = async () => {
      try {
        const response = await axios.get(`/api/get-result.php?id=${id}`);
        setData(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('診断結果が見つかりませんでした');
        setLoading(false);
      }
    };

    fetchResult();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error || '結果が見つかりません'}</Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          ホームに戻る
        </Button>
      </Container>
    );
  }

  // RIASECレーダーチャート用データ
  const riasecLabels: { [key: string]: string } = {
    R: '現実的(R)',
    I: '研究的(I)',
    A: '芸術的(A)',
    S: '社会的(S)',
    E: '企業的(E)',
    C: '慣習的(C)',
  };
  const riasecChartData = Object.entries(data.riasec_profile).map(
    ([key, value]) => ({
      subject: riasecLabels[key] || key,
      score: value,
      fullMark: 30,
    })
  );

  const riasecDescriptions: { [key: string]: { name: string; description: string } } = {
    R: { name: '現実的タイプ', description: '物や道具を使った具体的な作業が得意で、体を動かす仕事やものづくりに向いています。' },
    I: { name: '研究的タイプ', description: '物事を深く調べたり分析したりすることが好きで、データや知識を扱う仕事に向いています。' },
    A: { name: '芸術的タイプ', description: '自由な発想や創造力を活かすことが好きで、デザインや企画など独創性が求められる仕事に向いています。' },
    S: { name: '社会的タイプ', description: '人と関わり、サポートすることにやりがいを感じ、教育・福祉・サービス業に向いています。' },
    E: { name: '企業的タイプ', description: '人を率いたり、影響を与えることが得意で、営業・マネジメント・起業などに向いています。' },
    C: { name: '慣習的タイプ', description: '正確さや秩序を重視し、データ管理や事務処理など、決まったルールに沿った仕事に向いています。' },
  };

  const riasecSorted = Object.entries(data.riasec_profile)
    .sort(([, a], [, b]) => b - a);
  const topRiasec = riasecSorted.slice(0, 2);
  const riasecSummary = `この方の職業興味は「${riasecDescriptions[topRiasec[0][0]].name}」と「${riasecDescriptions[topRiasec[1][0]].name}」の傾向が強く出ています。${riasecDescriptions[topRiasec[0][0]].description}`;

  // ポータブルスキル棒グラフ用データ
  const skillLabels: { [key: string]: string } = {
    communication: 'コミュニケーション力',
    presentation: 'プレゼンテーション力',
    negotiation: '交渉力',
    listening: '傾聴力',
    problemIdentification: '問題発見力',
    analysis: '分析力',
    planning: '計画力',
    execution: '実行力',
  };

  const skillDescriptions: { [key: string]: string } = {
    communication: '相手にわかりやすく伝え、円滑なやり取りができる力',
    presentation: '自分の考えを整理し、相手に効果的に発表できる力',
    negotiation: '異なる立場の人と合意形成を図れる力',
    listening: '相手の話を丁寧に聞き、意図や気持ちを正確に理解する力',
    problemIdentification: '現状の課題や問題を見つけ出す力',
    analysis: 'データや情報を整理して本質を見抜く力',
    planning: '目標達成に向けた計画を立て、段取りを組む力',
    execution: '決めたことを最後までやり遂げる力',
  };

  const skillsChartData = Object.entries(data.portable_skills).map(
    ([key, value]) => ({
      name: skillLabels[key] || key,
      スコア: value,
    })
  );

  const skillsSorted = Object.entries(data.portable_skills)
    .sort(([, a], [, b]) => b - a);
  const topSkills = skillsSorted.slice(0, 3);
  const skillsSummary = `この方の強みは「${skillLabels[topSkills[0][0]]}」「${skillLabels[topSkills[1][0]]}」「${skillLabels[topSkills[2][0]]}」です。特に「${skillLabels[topSkills[0][0]]}」は${skillDescriptions[topSkills[0][0]]}であり、多くの職種で高く評価されるスキルです。`;

  const careerMatches = data.career_matches || [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ヘッダー */}
      <Box
        sx={{
          bgcolor: '#0066cc',
          color: 'white',
          py: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            共有された診断結果
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* 基本情報 */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {data.name}様の診断結果
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`${data.age}歳`} />
              <Chip label={data.sport} color="primary" />
              <Chip label={`競技歴 ${data.years_of_experience}年`} />
              <Chip label={data.competition_level} variant="outlined" />
            </Box>
          </CardContent>
        </Card>

        {/* アスリートとしての強み */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmojiEvents sx={{ color: '#ff9800', fontSize: 32, mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                アスリートとしての強み
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {data.athlete_advantages.map((advantage, index) => (
                <Chip
                  key={index}
                  label={advantage}
                  color="warning"
                  sx={{ fontSize: '0.95rem', py: 2.5 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* 診断結果詳細 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* RIASECプロファイル */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  職業興味プロファイル（RIASEC）
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  {/* @ts-ignore - recharts v3 type issue */}
                  <RadarChart data={riasecChartData}>
                    <PolarGrid />
                    {/* @ts-ignore - recharts v3 + React 19 type incompatibility */}
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={90} domain={[0, 30]} />
                    <Radar
                      name="スコア"
                      dataKey="score"
                      stroke="#0066cc"
                      fill="#0066cc"
                      fillOpacity={0.6}
                    />
                  </RadarChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f4ff', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                    {riasecSummary}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ポータブルスキル */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  ポータブルスキル
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  {/* @ts-ignore - recharts v3 type issue */}
                  <BarChart data={skillsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis domain={[0, 15]} />
                    <Tooltip />
                    <Bar dataKey="スコア" fill="#0066cc" />
                  </BarChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f4ff', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                    {skillsSummary}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 強みと開発領域 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrendingUp sx={{ color: '#4caf50', fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    強み
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {data.strengths.map((strength, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body1"
                      sx={{ mb: 1.5, color: '#333' }}
                    >
                      {strength}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Info sx={{ color: '#2196f3', fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    伸ばせる領域
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {data.development_areas.map((area, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body1"
                      sx={{ mb: 1.5, color: '#333' }}
                    >
                      {area}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* マッチング職業 */}
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            マッチする職業（上位10件）
          </Typography>

          <Grid container spacing={3}>
            {careerMatches.slice(0, 10).map((match, index) => (
              <Grid item xs={12} md={6} key={match.career.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: index < 3 ? '2px solid #0066cc' : '1px solid #e0e0e0',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          flexGrow: 1,
                          fontWeight: 600,
                          color: index < 3 ? '#0066cc' : '#333',
                        }}
                      >
                        {index + 1}. {match.career.name}
                      </Typography>
                      {match.career.athleteFriendly && (
                        <EmojiEvents sx={{ color: '#ff9800', fontSize: 24 }} />
                      )}
                    </Box>

                    <Chip
                      label={`マッチ度: ${match.matchScore}%`}
                      color={index < 3 ? 'primary' : 'default'}
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                      {match.career.description}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                    >
                      マッチする理由:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mb: 1, color: '#666', display: 'block' }}
                    >
                      {match.reason}
                    </Typography>

                    {match.nextSteps.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: 'block', mb: 1, mt: 1.5 }}
                        >
                          次のステップ:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {match.nextSteps.map((step, idx) => (
                            <Typography
                              key={idx}
                              component="li"
                              variant="caption"
                              sx={{ mb: 0.5, color: '#666' }}
                            >
                              {step}
                            </Typography>
                          ))}
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* フッターボタン */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/assessment')}
            sx={{ px: 6 }}
          >
            自分も診断する
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SharedResultsPage;
